import TabbedPanel, { TabDefinition } from 'toolkit/tabs/tabbedPanel';
import PendingItems from './components/pending-items';
import PendingTitle from './components/pending-title';
import ScrollableFeedItems from './components/scrollable-feed-items';
import { TrackingProvider } from 'react-components/tracking';
import { Box, Typography, useTheme } from '@mui/material';
import { PropsWithChildren, useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface MemberFeedProps {
  memberId: string;
}

// Pending item types that should show in the pending-events tab
const PENDING_ITEM_TYPES = [
  'scheduled_event',
  'workflow_phase',
  'transfer_request',
  'status_change_request',
];

// Extract FeedWrapper outside to prevent recreating on every render
const createFeedWrapper = (backgroundColor: string) => {
  const FeedWrapper = ({ children }: PropsWithChildren) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        minHeight: 0, // Ensures proper flex behavior on iOS
        padding: '0 12px',
        backgroundColor,
      }}
    >
      {children}
    </Box>
  );
  return FeedWrapper;
};

const MemberFeed = ({ memberId }: MemberFeedProps) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [currentTabKey, setCurrentTabKey] = useState<string>('past-events');

  // Check if we should switch to pending-events tab based on query params
  useEffect(() => {
    const feedItemExternalType = searchParams.get('feedItemExternalType');
    if (feedItemExternalType && PENDING_ITEM_TYPES.includes(feedItemExternalType)) {
      setCurrentTabKey('pending-events');
    } else if (feedItemExternalType && !PENDING_ITEM_TYPES.includes(feedItemExternalType)) {
      setCurrentTabKey('past-events');
    }
  }, [searchParams]);

  // Memoize tab definitions to prevent unnecessary remounting of tab components
  // when parent re-renders due to Redux state changes (e.g., websocket events)
  const tabDefinitions: TabDefinition[] = useMemo(() => {
    const FeedWrapper = createFeedWrapper(
      theme.palette.surfaces.neutralLighter
    );

    return [
      {
        key: 'past-events',
        label: <Typography variant="bodyMedium">Past events</Typography>,
        component: (
          <FeedWrapper>
            <TrackingProvider contextName="member-feed">
              <ScrollableFeedItems />
            </TrackingProvider>
          </FeedWrapper>
        ),
      },
      {
        key: 'pending-events',
        label: <PendingTitle memberId={memberId} />,
        component: (
          <FeedWrapper>
            <PendingItems memberId={memberId} />
          </FeedWrapper>
        ),
      },
    ];
  }, [memberId, theme.palette.surfaces.neutralLighter]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <TabbedPanel
        tabDefinitions={tabDefinitions}
        scrollButtons={false}
        currentTabKey={currentTabKey}
        setCurrentTabKey={setCurrentTabKey}
      />
    </Box>
  );
};

export default MemberFeed;
